import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import ConfirmDialog from './ConfirmDialog';
import {
  EnvelopeOpenIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PlusCircleIcon,
  TrashIcon
} from '@heroicons/react/24/outline';

const OrganizationEmailTemplatesModal = ({ organization, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedKey, setSelectedKey] = useState('new_team_welcome');
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [subjectTemplate, setSubjectTemplate] = useState('');
  const [bodyTemplate, setBodyTemplate] = useState('');
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDescription, setNewTemplateDescription] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  const token = localStorage.getItem('token');

  useEffect(() => {
    if (organization?.id) {
      fetchTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization?.id]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.key === selectedKey),
    [templates, selectedKey]
  );

  useEffect(() => {
    if (!selectedTemplate) return;
    setTemplateName(selectedTemplate.name || '');
    setTemplateDescription(selectedTemplate.description || '');
    setSubjectTemplate(selectedTemplate.subjectTemplate || '');
    setBodyTemplate(selectedTemplate.bodyTemplate || '');
  }, [selectedTemplate]);

  const fetchTemplates = async (preferredKey = null) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`/api/organizations/${organization.id}/email-templates`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      const templateList = response.data?.templates || [];
      setTemplates(templateList);

      if (templateList.length > 0) {
        if (preferredKey && templateList.some((template) => template.key === preferredKey)) {
          setSelectedKey(preferredKey);
        } else if (!templateList.some((template) => template.key === selectedKey)) {
          setSelectedKey(templateList[0].key);
        }
      }
    } catch (err) {
      console.error('Error fetching email templates:', err);
      setError(err.response?.data?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCustomTemplate = async () => {
    if (!newTemplateName.trim()) {
      setError('Template name is required');
      return;
    }

    try {
      setCreating(true);
      setError(null);
      setSuccess(null);

      const response = await axios.post(
        `/api/organizations/${organization.id}/email-templates/custom`,
        {
          name: newTemplateName.trim(),
          description: newTemplateDescription.trim(),
          subjectTemplate: `${organization?.name || 'Organization'} Update`,
          bodyTemplate: 'Hi {{recipientName}},\n\n'
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const createdTemplate = response.data?.template;
      setSuccess('Custom template created');
      setNewTemplateName('');
      setNewTemplateDescription('');
      await fetchTemplates(createdTemplate?.key || null);
    } catch (err) {
      console.error('Error creating custom template:', err);
      setError(err.response?.data?.message || 'Failed to create custom template');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async () => {
    if (!selectedTemplate) return;

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (selectedTemplate.type === 'custom') {
        await axios.put(
          `/api/organizations/${organization.id}/email-templates/custom/${selectedTemplate.id}`,
          {
            name: templateName.trim(),
            description: templateDescription.trim(),
            subjectTemplate,
            bodyTemplate
          },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      } else {
        await axios.put(
          `/api/organizations/${organization.id}/email-templates/${selectedTemplate.key}`,
          { subjectTemplate, bodyTemplate },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          }
        );
      }

      setSuccess('Template saved');
      await fetchTemplates(selectedTemplate.key);
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.response?.data?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCustomTemplate = () => {
    if (!selectedTemplate || selectedTemplate.type !== 'custom') return;
    setConfirmState({
      title: 'Delete Template',
      message: `Delete template "${selectedTemplate.name}"?`,
      isDangerous: true,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        try {
          setDeleting(true);
          setError(null);
          setSuccess(null);

          await axios.delete(
            `/api/organizations/${organization.id}/email-templates/custom/${selectedTemplate.id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          );

          setSuccess('Template deleted');
          await fetchTemplates();
        } catch (err) {
          console.error('Error deleting custom template:', err);
          setError(err.response?.data?.message || 'Failed to delete custom template');
        } finally {
          setDeleting(false);
        }
      },
    });
  };

  const handleResetToDefault = () => {
    if (!selectedTemplate || selectedTemplate.type === 'custom') return;
    setSubjectTemplate(selectedTemplate.defaultSubjectTemplate || '');
    setBodyTemplate(selectedTemplate.defaultBodyTemplate || '');
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-8 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <EnvelopeOpenIcon className="h-6 w-6 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              Email Templates - {organization?.name}
            </h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center">
            <ExclamationCircleIcon className="h-5 w-5 text-red-600 mr-2" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-center">
            <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {loading ? (
          <div className="py-12 text-center text-gray-500">Loading templates...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1 border border-gray-200 rounded-lg p-3 space-y-3">
              <div className="border border-gray-200 rounded-lg p-3 space-y-2 bg-gray-50">
                <p className="text-xs font-semibold text-gray-700">Create Custom Template</p>
                <input
                  type="text"
                  value={newTemplateName}
                  onChange={(e) => setNewTemplateName(e.target.value)}
                  placeholder="Template name"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={newTemplateDescription}
                  onChange={(e) => setNewTemplateDescription(e.target.value)}
                  placeholder="Optional description"
                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="button"
                  onClick={handleCreateCustomTemplate}
                  disabled={creating}
                  className="w-full px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 flex items-center justify-center"
                >
                  <PlusCircleIcon className="h-4 w-4 mr-1" />
                  {creating ? 'Creating...' : 'Create'}
                </button>
              </div>

              {templates.map((template) => (
                <button
                  key={template.key}
                  type="button"
                  onClick={() => setSelectedKey(template.key)}
                  className={`w-full text-left p-3 rounded-lg border ${
                    template.key === selectedKey
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900">{template.name}</div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full border border-gray-300 text-gray-600 uppercase">
                      {template.type === 'custom' ? 'Custom' : 'System'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">{template.description}</div>
                  <div className="text-xs mt-2 text-gray-500">
                    {template.type === 'custom'
                      ? 'Custom template'
                      : (template.isCustomized ? 'Customized' : 'Using default')}
                  </div>
                </button>
              ))}
            </div>

            <div className="md:col-span-2 space-y-4">
              {selectedTemplate?.type === 'custom' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Template Name</label>
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={templateDescription}
                      onChange={(e) => setTemplateDescription(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject Template</label>
                <input
                  type="text"
                  value={subjectTemplate}
                  onChange={(e) => setSubjectTemplate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Message Template</label>
                <textarea
                  value={bodyTemplate}
                  onChange={(e) => setBodyTemplate(e.target.value)}
                  rows={14}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Available tokens</p>
                <div className="flex flex-wrap gap-2">
                  {(selectedTemplate?.tokens || []).map((tokenName) => (
                    <span
                      key={tokenName}
                      className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded border border-gray-200"
                    >
                      {`{{${tokenName}}}`}
                    </span>
                  ))}
                </div>
              </div>

              <div className="flex justify-between pt-2">
                <div className="flex items-center gap-2">
                  {selectedTemplate?.type !== 'custom' && (
                    <button
                      type="button"
                      onClick={handleResetToDefault}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Reset To Default Text
                    </button>
                  )}
                  {selectedTemplate?.type === 'custom' && (
                    <button
                      type="button"
                      onClick={handleDeleteCustomTemplate}
                      disabled={deleting}
                      className="px-3 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50 flex items-center"
                    >
                      <TrashIcon className="h-4 w-4 mr-1" />
                      {deleting ? 'Deleting...' : 'Delete Template'}
                    </button>
                  )}
                </div>

                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
              </div>
            </div>
          </div>
        )}
      <ConfirmDialog
        isOpen={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message}
        isDangerous={confirmState?.isDangerous}
        confirmLabel={confirmState?.confirmLabel}
        onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
        onCancel={() => setConfirmState(null)}
      />
      </div>
    </div>
  );
};

export default OrganizationEmailTemplatesModal;
